import api from './api';

export type VerificationPhotoScope = 'item' | 'pack';

/** PRX-51: one product-verification photo, as the API returns it. */
export interface VerificationPhoto {
  id: string;
  orderId: string;
  rxNumber: string | null;
  scope: VerificationPhotoScope;
  contentType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  capturedByUserId: string | null;
  capturedByName: string | null;
  capturedAt: string;
}

export interface UploadVerificationPhotoInput {
  scope: VerificationPhotoScope;
  /** Required for scope 'item'; must be omitted for scope 'pack'. */
  rxNumber?: string;
}

/** PRX-68: a photo from the patient-scoped list, carrying the order it came from. */
export interface PatientVerificationPhoto extends VerificationPhoto {
  orderNumber: string | null;
}

const base = (pharmacyId: string, orderId: string) =>
  `/api/orders/${pharmacyId}/${orderId}/verification-photos`;

export const verificationPhotoApi = {
  async list(pharmacyId: string, orderId: string): Promise<VerificationPhoto[]> {
    const res = await api.get<VerificationPhoto[]>(base(pharmacyId, orderId));
    return res.data;
  },

  async upload(
    pharmacyId: string,
    orderId: string,
    file: File,
    input: UploadVerificationPhotoInput,
  ): Promise<VerificationPhoto> {
    const form = new FormData();
    form.append('photo', file);
    form.append('scope', input.scope);
    if (input.rxNumber) form.append('rxNumber', input.rxNumber);
    const res = await api.post<VerificationPhoto>(base(pharmacyId, orderId), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  /**
   * The bytes, fetched with the JWT and turned into an object URL by the
   * caller. There is no signed URL to embed: the route streams the image so a
   * link cannot outlive the session or survive a soft delete.
   */
  async fetchImage(pharmacyId: string, orderId: string, photoId: string): Promise<Blob> {
    const res = await api.get<Blob>(`${base(pharmacyId, orderId)}/${photoId}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  async remove(pharmacyId: string, orderId: string, photoId: string): Promise<void> {
    await api.delete(`${base(pharmacyId, orderId)}/${photoId}`);
  },

  /**
   * PRX-68: every live photo on every order of one patient at one pharmacy,
   * newest first, with the order number. Used by Rx-centric surfaces
   * (Patient 360's prescription dialog) that have no order id of their own.
   */
  async listForPatient(pharmacyId: string, patientId: string): Promise<PatientVerificationPhoto[]> {
    const res = await api.get<PatientVerificationPhoto[]>(
      `/api/orders/${pharmacyId}/verification-photos/by-patient/${encodeURIComponent(patientId)}`,
    );
    return res.data;
  },
};
